/**
 * Fair Enough size calculator.
 * Custom element so it upgrades even when morph-injected (quick-add modal).
 * Contract: data-kind="pants" | "shirt", internal data-fe-range/output/tab/panel/cta/result hooks.
 */
if (!customElements.get('fe-size-guide')) {
  class FeSizeGuide extends HTMLElement {
    #initialized = false;
    #kind = 'pants';
    #sizes = [];
    #waist = [];
    #leg = [];
    #overLimit = 108;
    #result = null;
    #cta = null;
    #currentSize = null;
    #currentNote = '';

    connectedCallback() {
      if (this.#initialized) return;
      this.#initialized = true;

      this.#kind = this.dataset.kind === 'shirt' ? 'shirt' : 'pants';
      this.#sizes = ['30', '32', '34', '36', '38'];
      this.#waist = [40, 43, 45, 48, 50];
      this.#leg = [107, 108, 109, 110, 111];
      this.#overLimit = this.#kind === 'pants' ? 108 : 95;
      this.#result = this.querySelector('[data-fe-result]');
      this.#cta = this.querySelector('[data-fe-cta]');
      this.#currentSize = null;
      this.#currentNote = '';

      this.querySelectorAll('[data-fe-range]').forEach((input) => {
        this.#fill(input);
        input.addEventListener('input', () => {
          this.#fill(input);
          this.#render();
        });
      });

      this.#cta?.addEventListener('click', () => this.#select());

      this.querySelectorAll('[data-fe-tab]').forEach((btn) => {
        btn.addEventListener('click', () => this.#showTab(btn));
      });

      this.#render();
    }

    #fill(input) {
      const min = parseFloat(input.min);
      const max = parseFloat(input.max);
      const pct = ((parseFloat(input.value) - min) / (max - min)) * 100;
      input.style.setProperty('--fe-fill', pct + '%');
      const output = this.querySelector('[data-fe-output="' + input.dataset.feRange + '"]');
      if (output) output.textContent = String(Math.round(parseFloat(input.value)));
    }

    #compute() {
      const height = parseFloat(this.querySelector('[data-fe-range="height"]').value);
      const weight = parseFloat(this.querySelector('[data-fe-range="weight"]').value);

      if (this.#kind === 'pants') {
        if (weight > 108) return { size: null };
        let idx;
        if (weight < 70) {
          idx = 0;
        } else if (weight < 80) {
          idx = 1;
        } else if (weight < 90) {
          idx = 2;
        } else if (weight < 99) {
          idx = 3;
        } else {
          idx = 4;
        }
        let sizedUp = false;
        if (height >= 185 && idx < 4) {
          idx += 1;
          sizedUp = true;
        }
        const note = sizedUp
          ? 'Sized up for extra length at your height.'
          : 'A comfortable everyday baggy fit based on your height and weight.';
        return { size: this.#sizes[idx], note: note, waist: this.#waist[idx], leg: this.#leg[idx] };
      }

      if (weight >= 95) return { size: null };
      const size = weight < 55 ? 'S' : weight < 65 ? 'M' : weight < 80 ? 'L' : 'XL';
      return { size: size, note: 'A comfortable everyday fit based on your height and weight.' };
    }

    #radioFor(size) {
      const scope =
        this.closest('[id^="shopify-section-"]') ||
        this.closest('#quick-add-modal-content') ||
        document;
      return scope.querySelector('input[type="radio"][data-option-name="Size"][value="' + size + '"]');
    }

    #availability(size) {
      const radio = this.#radioFor(size);
      if (!radio) return true;
      return radio.getAttribute('data-option-available') !== 'false';
    }

    #nearestAvailable(fromIndex) {
      for (let d = 1; d < this.#sizes.length; d++) {
        if (fromIndex + d < this.#sizes.length && this.#availability(this.#sizes[fromIndex + d])) {
          return this.#sizes[fromIndex + d];
        }
        if (fromIndex - d >= 0 && this.#availability(this.#sizes[fromIndex - d])) {
          return this.#sizes[fromIndex - d];
        }
      }
      return null;
    }

    #render() {
      if (!this.#result || !this.#cta) return;
      const r = this.#compute();
      this.#currentSize = null;
      this.#currentNote = '';

      if (!r.size) {
        this.#result.innerHTML =
          '<p class="fe-result__size">&mdash;</p>' +
          '<p class="fe-result__note">We do not have a recommended size above ' +
          this.#overLimit +
          ' kg yet. Contact us on WhatsApp and we will help you find your fit.</p>';
        this.#cta.hidden = true;
        return;
      }

      this.#currentSize = r.size;
      this.#currentNote = r.note;
      let metaHtml = '';
      if (this.#kind === 'pants') {
        metaHtml =
          '<div class="fe-result__meta">' +
          '<span>Waist ' + r.waist + ' cm</span>' +
          '<span class="dot">&bull;</span>' +
          '<span>Leg ' + r.leg + ' cm</span>' +
          '</div>';
      } else {
        metaHtml = '<div class="fe-result__meta fe-result__meta--hidden"></div>';
      }
      this.#result.innerHTML =
        '<p class="fe-result__label">Recommended size</p>' +
        '<p class="fe-result__size">' + r.size + '</p>' +
        '<p class="fe-result__note">' + r.note + '</p>' +
        metaHtml;

      if (!this.#radioFor(r.size)) {
        this.#cta.hidden = true;
        return;
      }

      if (!this.#availability(r.size)) {
        const fallback = this.#nearestAvailable(this.#sizes.indexOf(r.size));
        if (fallback) {
          this.#currentSize = fallback;
          this.#currentNote = 'Size ' + r.size + ' is sold out — nearest available shown.';
        } else {
          this.#currentSize = null;
        }
      }

      if (this.#currentSize) {
        this.#cta.hidden = false;
        this.#cta.classList.remove('is-done');
        this.#cta.textContent = 'Select size ' + this.#currentSize;
        if (this.#currentNote !== r.note) {
          const noteEl = this.#result.querySelector('.fe-result__note');
          if (noteEl) noteEl.textContent = this.#currentNote;
        }
      } else {
        this.#cta.hidden = true;
      }
    }

    #select() {
      if (!this.#currentSize || this.#cta.classList.contains('is-done')) return;
      const radio = this.#radioFor(this.#currentSize);
      if (!radio) return;
      if (!radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.#cta.classList.add('is-done');
      this.#cta.textContent = 'Size ' + this.#currentSize + ' selected \u2713';
    }

    #showTab(activeBtn) {
      this.querySelectorAll('[data-fe-tab]').forEach((b) => {
        b.classList.toggle('is-active', b === activeBtn);
      });
      this.querySelectorAll('[data-fe-panel]').forEach((panel) => {
        panel.hidden = panel.getAttribute('data-fe-panel') !== activeBtn.getAttribute('data-fe-tab');
      });
    }
  }

  customElements.define('fe-size-guide', FeSizeGuide);
}
